/*
 * Almarzooqi Atelier — global theme script
 * Dialogs (menu, search, drawers), cart drawer and cart page, predictive search,
 * header measurements. Loaded with `defer`. Every listener is registered once
 * (document-level delegation), so theme editor section reloads never duplicate them.
 */
(function () {
  'use strict';

  if (window.__almarzooqiThemeReady) return;
  window.__almarzooqiThemeReady = true;

  var theme = window.theme || { routes: {}, strings: {} };
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------- Utilities ---------------- */
  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function announce(message) {
    var region = document.querySelector('[data-live-region]');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 60);
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /* Fills "[key]" placeholders that Liquid left in translated strings. */
  function formatString(template, values) {
    return String(template || '').replace(/\[(\w+)\]|\{\{\s*(\w+)\s*\}\}/g, function (match, a, b) {
      var key = a || b;
      return values && values[key] != null ? values[key] : '';
    });
  }

  theme.utils = { debounce: debounce, announce: announce, parseHTML: parseHTML, formatString: formatString };

  /* ---------------- Header height (for sticky offsets and anchors) ---------------- */
  var headerObserver = null;
  function measureHeader() {
    var header = document.querySelector('.section-header');
    if (!header) return;
    var sticky = window.getComputedStyle(header).position === 'sticky';
    var height = sticky ? header.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--header-h', Math.round(height) + 'px');
  }
  function watchHeader() {
    if (headerObserver) headerObserver.disconnect();
    var header = document.querySelector('.section-header');
    if (!header) return;
    measureHeader();
    if ('ResizeObserver' in window) {
      headerObserver = new ResizeObserver(measureHeader);
      headerObserver.observe(header);
    }
  }

  /* ---------------- Dialogs ---------------- */
  function openDialogs() {
    return Array.prototype.slice.call(document.querySelectorAll('dialog[open]'));
  }

  function syncScrollLock() {
    document.documentElement.classList.toggle('is-locked', openDialogs().length > 0);
  }

  function openDialog(dialog, trigger) {
    if (!dialog || dialog.open) return;
    dialog._returnFocus = trigger || document.activeElement;
    dialog.classList.remove('is-closing');
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    syncScrollLock();
    var focusTarget = dialog.querySelector('[data-autofocus]');
    if (focusTarget) {
      window.requestAnimationFrame(function () { focusTarget.focus(); });
    }
    dialog.dispatchEvent(new CustomEvent('theme:dialog:open', { bubbles: true, detail: { trigger: trigger } }));
  }

  function closeDialog(dialog, options) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    var restoreFocus = !(options && options.restoreFocus === false);
    dialog._restoreFocus = restoreFocus;
    var finish = function () {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        dialog.dispatchEvent(new Event('close'));
      }
    };
    if (reduceMotion.matches) {
      finish();
    } else {
      dialog.classList.add('is-closing');
      window.setTimeout(finish, 190);
    }
  }

  document.addEventListener('close', function (event) {
    var dialog = event.target;
    if (!(dialog instanceof HTMLElement) || dialog.tagName !== 'DIALOG') return;
    dialog.classList.remove('is-closing');
    syncScrollLock();
    var target = dialog._returnFocus;
    if (dialog._restoreFocus !== false && target && document.contains(target) && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
    dialog._restoreFocus = true;
    dialog.dispatchEvent(new CustomEvent('theme:dialog:close', { bubbles: true }));
  }, true);

  document.addEventListener('cancel', function (event) {
    var dialog = event.target;
    if (dialog && dialog.tagName === 'DIALOG') {
      event.preventDefault();
      closeDialog(dialog);
    }
  }, true);

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.getAttribute('data-dialog-open')), opener);
      return;
    }

    var closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest('dialog'));
      return;
    }

    /* A click on the dialog element itself is a click on its backdrop or empty frame. */
    if (event.target.tagName === 'DIALOG' && event.target.open) {
      closeDialog(event.target);
    }
  });

  theme.dialog = { open: openDialog, close: closeDialog };

  /* Same-page anchor links inside the menu drawer (e.g. /#custom-designs) */
  document.addEventListener('click', function (event) {
    var link = event.target.closest('.menu-drawer a[href*="#"]');
    if (!link) return;
    var url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname || !url.hash) return;
    var target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    var dialog = link.closest('dialog');
    closeDialog(dialog, { restoreFocus: false });
    window.setTimeout(function () {
      target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
      if (history.pushState) history.pushState(null, '', url.hash);
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }, reduceMotion.matches ? 0 : 220);
  });

  /* Header dropdowns: one open at a time; close on outside click and Escape */
  document.addEventListener('toggle', function (event) {
    var details = event.target;
    if (!details.matches || !details.matches('[data-header-dropdown]') || !details.open) return;
    document.querySelectorAll('[data-header-dropdown][open]').forEach(function (other) {
      if (other !== details) other.removeAttribute('open');
    });
  }, true);
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-header-dropdown]')) return;
    document.querySelectorAll('[data-header-dropdown][open]').forEach(function (d) { d.removeAttribute('open'); });
  });
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var open = document.querySelector('[data-header-dropdown][open]');
    if (open) {
      open.removeAttribute('open');
      var summary = open.querySelector('summary');
      if (summary) summary.focus();
    }
  });

  /* ---------------- Cart ---------------- */
  function CartError(message) {
    this.name = 'CartError';
    this.message = message;
  }
  CartError.prototype = Object.create(Error.prototype);

  var Cart = {
    sectionIds: function () {
      var ids = ['cart-drawer', 'cart-icon-bubble'];
      var main = document.querySelector('[data-main-cart]');
      if (main && main.dataset.sectionId) ids.push(main.dataset.sectionId);
      return ids;
    },

    errorMessage: function (data) {
      if (!data) return theme.strings.cartError;
      if (typeof data.description === 'string' && data.description) return data.description;
      if (typeof data.errors === 'string' && data.errors) return data.errors;
      if (typeof data.message === 'string' && data.message) return data.message;
      return theme.strings.cartError;
    },

    add: function (formData) {
      var self = this;
      formData.append('sections', this.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      return fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData
      })
        .then(function (response) {
          return response.json().then(function (data) { return { ok: response.ok, data: data }; });
        })
        .then(function (result) {
          if (!result.ok || result.data.status) throw new CartError(self.errorMessage(result.data));
          self.render(result.data.sections);
          document.dispatchEvent(new CustomEvent('theme:cart:add', { detail: result.data }));
          return result.data;
        });
    },

    /*
     * Adds several lines in one request. Used when a piece is added together
     * with a paid add-on (Add Al Oqom), so the add-on is really charged and is
     * never added without its piece.
     */
    addItems: function (items) {
      var self = this;
      return fetch(theme.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: items,
          sections: this.sectionIds(),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json().then(function (data) { return { ok: response.ok, data: data }; });
        })
        .then(function (result) {
          if (!result.ok || result.data.status) throw new CartError(self.errorMessage(result.data));
          self.render(result.data.sections);
          document.dispatchEvent(new CustomEvent('theme:cart:add', { detail: result.data }));
          return result.data;
        });
    },

    /* Sets several line quantities at once (a piece and its add-on lines). */
    update: function (updates) {
      var self = this;
      return fetch(theme.routes.cartUpdate + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          updates: updates,
          sections: this.sectionIds(),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json().then(function (data) { return { ok: response.ok, data: data }; });
        })
        .then(function (result) {
          if (!result.ok || result.data.status) throw new CartError(self.errorMessage(result.data));
          self.render(result.data.sections);
          document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: result.data }));
          return result.data;
        });
    },

    change: function (key, quantity) {
      var self = this;
      return fetch(theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: this.sectionIds(),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json().then(function (data) { return { ok: response.ok, data: data }; });
        })
        .then(function (result) {
          if (!result.ok || result.data.status) throw new CartError(self.errorMessage(result.data));
          self.render(result.data.sections);
          document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: result.data }));
          return result.data;
        });
    },

    updateNote: function (note) {
      return fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note })
      });
    },

    refresh: function () {
      var self = this;
      var url = window.location.pathname + '?sections=' + encodeURIComponent(this.sectionIds().join(','));
      return fetch(url).then(function (r) { return r.json(); }).then(function (sections) { self.render(sections); });
    },

    render: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (id) {
        var html = sections[id];
        if (html == null) return;
        var doc = parseHTML(html);
        if (id === 'cart-icon-bubble') {
          var wrapper = doc.querySelector('.shopify-section');
          var content = wrapper ? wrapper.innerHTML : doc.body.innerHTML;
          document.querySelectorAll('[data-cart-bubble]').forEach(function (el) { el.innerHTML = content; });
        } else if (id === 'cart-drawer') {
          var nextDrawer = doc.querySelector('[data-cart-drawer-inner]');
          var currentDrawer = document.querySelector('[data-cart-drawer-inner]');
          if (nextDrawer && currentDrawer) currentDrawer.innerHTML = nextDrawer.innerHTML;
        } else {
          var nextMain = doc.querySelector('[data-main-cart-inner]');
          var currentMain = document.querySelector('[data-main-cart][data-section-id="' + id + '"] [data-main-cart-inner]');
          if (nextMain && currentMain) currentMain.innerHTML = nextMain.innerHTML;
        }
      });
    },

    openDrawer: function (trigger) {
      var drawer = document.getElementById('CartDrawer');
      if (drawer && !document.querySelector('[data-main-cart]')) {
        openDialog(drawer, trigger);
        return true;
      }
      return false;
    }
  };

  theme.cart = Cart;
  window.ThemeCart = Cart;

  /* Header cart icon opens the drawer (the link still works without JavaScript). */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-cart-trigger]');
    if (!trigger || theme.cartType !== 'drawer') return;
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;
    if (Cart.openDrawer(trigger)) event.preventDefault();
  });

  /* Line quantity and removal, keyed by line item key so each customised line is independent. */
  var pendingLines = {};

  function lineContainer(line) {
    return line.closest('[data-cart-drawer-inner]') || line.closest('[data-main-cart-inner]') || document;
  }

  function showCartError(container, message) {
    var box = container.querySelector('[data-cart-error]');
    if (box) {
      box.textContent = message;
      box.hidden = false;
    }
    announce(message);
  }

  function updateLine(line, quantity) {
    if (!line) return;
    var key = line.getAttribute('data-line-key');
    var container = lineContainer(line);
    var inDrawer = !!line.closest('[data-cart-drawer-inner]');
    var focused = document.activeElement;
    var focusRole = null;
    if (focused && line.contains(focused)) {
      if (focused.matches('[data-cart-qty]')) focusRole = 'qty';
      else if (focused.matches('[data-cart-step="1"]')) focusRole = 'plus';
      else if (focused.matches('[data-cart-step="-1"]')) focusRole = 'minus';
    }
    line.classList.add('is-updating');

    /*
     * A piece with a paid add-on (Add Al Oqom) is linked to its add-on line by a
     * shared bundle id. Both lines always change together, so the add-on is
     * never charged more or fewer times than its piece, and never left behind.
     */
    var bundle = line.getAttribute('data-bundle-id');
    var request;
    if (bundle) {
      var updates = {};
      updates[key] = quantity;
      container.querySelectorAll('[data-addon-line][data-bundle-id="' + CSS.escape(bundle) + '"]').forEach(function (addon) {
        updates[addon.getAttribute('data-line-key')] = quantity;
      });
      request = Cart.update(updates);
    } else {
      request = Cart.change(key, quantity);
    }

    request
      .then(function () {
        announce(theme.strings.cartUpdated);
        var scope = inDrawer ? document.querySelector('[data-cart-drawer-inner]') : document.querySelector('[data-main-cart-inner]');
        if (!scope) return;
        var sameLine = scope.querySelector('[data-line-key="' + CSS.escape(key) + '"]');
        var target = null;
        if (sameLine && focusRole) {
          target = sameLine.querySelector(focusRole === 'qty' ? '[data-cart-qty]' : focusRole === 'plus' ? '[data-cart-step="1"]' : '[data-cart-step="-1"]');
        }
        if (!target && !sameLine) {
          target = scope.querySelector('[data-line-key] [data-cart-qty]') || scope.querySelector('.drawer__title, .page-header__title');
          if (target && !target.hasAttribute('tabindex') && target.matches('h1, h2')) target.setAttribute('tabindex', '-1');
        }
        if (target) target.focus({ preventScroll: true });
      })
      .catch(function (error) {
        line.classList.remove('is-updating');
        var input = line.querySelector('[data-cart-qty]');
        if (input && input.dataset.lastValue) input.value = input.dataset.lastValue;
        showCartError(container, error.message || theme.strings.cartError);
      });
  }

  function queueLineUpdate(line, quantity, immediate) {
    var key = line.getAttribute('data-line-key');
    clearTimeout(pendingLines[key]);
    if (immediate) {
      updateLine(line, quantity);
      return;
    }
    pendingLines[key] = setTimeout(function () { updateLine(line, quantity); }, 380);
  }

  document.addEventListener('focusin', function (event) {
    if (event.target.matches && event.target.matches('[data-cart-qty]')) {
      event.target.dataset.lastValue = event.target.value;
    }
  });

  document.addEventListener('click', function (event) {
    var step = event.target.closest('[data-cart-step]');
    if (step) {
      var line = step.closest('[data-line-key]');
      var input = line && line.querySelector('[data-cart-qty]');
      if (!input) return;
      if (!input.dataset.lastValue) input.dataset.lastValue = input.value;
      var next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.getAttribute('data-cart-step')));
      input.value = next;
      queueLineUpdate(line, next, false);
      return;
    }
    var remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      queueLineUpdate(remove.closest('[data-line-key]'), 0, true);
    }
  });

  document.addEventListener('change', function (event) {
    var el = event.target;
    if (el.matches('[data-cart-qty]')) {
      var line = el.closest('[data-line-key]');
      var value = parseInt(el.value, 10);
      if (isNaN(value) || value < 0) value = 0;
      el.value = value;
      queueLineUpdate(line, value, true);
    } else if (el.matches('[data-cart-note]')) {
      Cart.updateNote(el.value);
    } else if (el.matches('[data-sort-select]') && el.form) {
      el.form.submit();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' && event.target.matches && event.target.matches('[data-cart-qty]')) {
      event.preventDefault();
      event.target.blur();
    }
  });

  /* ---------------- Predictive search ---------------- */
  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', class extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.input = this.querySelector('[data-search-input]');
        this.results = this.querySelector('[data-search-results]');
        this.status = this.querySelector('[data-search-status]');
        if (!this.input || this.dataset.enabled !== 'true' || !theme.routes.predictiveSearch) return;
        this.onInput = debounce(this.search.bind(this), 260);
        this.input.addEventListener('input', this.onInput);
      }

      search() {
        var self = this;
        var term = this.input.value.trim();
        if (this.controller) this.controller.abort();
        if (!term) {
          this.results.innerHTML = '';
          return;
        }
        this.controller = new AbortController();
        var url = theme.routes.predictiveSearch +
          '?q=' + encodeURIComponent(term) +
          '&resources[type]=product,page' +
          '&resources[limit]=6' +
          '&resources[options][fields]=title,product_type,tag,variants.title,variants.sku' +
          '&section_id=predictive-search';
        fetch(url, { signal: this.controller.signal })
          .then(function (response) {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then(function (html) {
            var doc = parseHTML(html);
            var fragment = doc.getElementById('predictive-search-results');
            self.results.innerHTML = fragment ? fragment.innerHTML : '';
            if (self.status) {
              var count = fragment ? parseInt(fragment.getAttribute('data-results-count'), 10) || 0 : 0;
              self.status.textContent = count > 0 ? theme.strings.searchResults : theme.strings.searchNoResults;
            }
          })
          .catch(function (error) {
            if (error.name !== 'AbortError') self.results.innerHTML = '';
          });
      }
    });
  }

  /* ---------------- Theme editor ---------------- */
  document.addEventListener('shopify:section:load', function () {
    watchHeader();
    syncScrollLock();
  });
  document.addEventListener('shopify:section:unload', function () {
    window.setTimeout(syncScrollLock, 0);
  });

  /* ---------------- Init ---------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchHeader);
  } else {
    watchHeader();
  }
  window.addEventListener('load', measureHeader);
})();
